import React from "react";

/**
 * Blue glyph on a gold button — the established CTA treatment, first used for
 * the Orders / Claims icons on the order-success page.
 *
 * Extracted once there were three call sites pasting the same 16px / #60a3d7 /
 * strokeWidth-2 SVG next to a label. The wrapper matters as much as the icon:
 * the gold buttons render `display: inline-block`, so an SVG dropped beside
 * text sits on the baseline instead of being optically centred against it —
 * hence the inline-flex span.
 *
 * Usage: <ButtonLabel glyph="mail">Send code</ButtonLabel>
 */

const BLUE = "#60a3d7";

export const GLYPHS = {
  /** → forward / continue */
  arrowRight: ["M5 12h14", "M12 5l7 7-7 7"],
  /** ✉ send-an-email actions (verification codes, links) */
  mail: [
    "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z",
    "M22 6l-10 7L2 6",
  ],
  /** $ paying — the actual charge. Reads better at 16px than a padlock, which
   *  at this size collapses into something closer to a head-and-shoulders. */
  dollar: ["M12 1v22", "M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"],
  /** 🔒 password / security actions */
  lock: ["M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z", "M7 11V7a5 5 0 0 1 10 0v4"],
  /** 💳 payment-method actions */
  card: ["M2 5h20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z", "M1 10h22"],
  /** ✓ confirming a decision the user has already made — not "submit", which
   *  the arrow covers. Used on the statutory withdrawal confirm. */
  check: ["M20 6L9 17l-5-5"],
  /** ↻ starting something again that was previously had — reactivating a
   *  lapsed subscription. The open circle with the arrow head reads as "again"
   *  where the plain arrow would read as "next". */
  redo: ["M21 2v6h-6", "M21 13a9 9 0 1 1-3-7.7L21 8"],
} as const;

export type GlyphName = keyof typeof GLYPHS;

/**
 * `muted` drops the blue and inherits the button's own text colour instead.
 * Use it whenever the button can be disabled: the gold buttons go brown with
 * faint text when inactive, and a bright blue glyph left sitting on that reads
 * as still-clickable. `currentColor` rather than a hard-coded grey so it tracks
 * whichever disabled treatment the surrounding button uses.
 */
export const ButtonGlyph: React.FC<{ glyph: GlyphName; size?: number; muted?: boolean }> = ({
  glyph,
  size = 16,
  muted = false,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={muted ? "currentColor" : BLUE}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, transition: "stroke 0.2s ease" }}
    aria-hidden
  >
    {GLYPHS[glyph].map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

/** Label + glyph, correctly centred inside an inline-block gold button.
 *  `iconFirst` puts the glyph before the text (the order-success convention for
 *  destination links); the default trails it, which suits actions. */
export const ButtonLabel: React.FC<{
  glyph: GlyphName;
  iconFirst?: boolean;
  /** Pass the button's own disabled state — see ButtonGlyph's `muted`. */
  muted?: boolean;
  children: React.ReactNode;
}> = ({ glyph, iconFirst = false, muted = false, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    {iconFirst && <ButtonGlyph glyph={glyph} muted={muted} />}
    {children}
    {!iconFirst && <ButtonGlyph glyph={glyph} muted={muted} />}
  </span>
);

export default ButtonLabel;
